import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Button,
  InputAdornment,
} from '@mui/material';
import { LocalShipping, Refresh, Save } from '@mui/icons-material';
import { useNotify } from 'react-admin';

interface ShopSettings {
  id?: string;
  key?: string;
  shippingFee: number;
  freeShippingThreshold: number;
  remoteAreaFee: number;
  deliveryDays: string;
  deliveryMethod: string;
  returnPeriod: number;
  returnShippingPolicy: string;
  returnAddress: string;
  returnContact: string;
}

const defaultSettings: ShopSettings = {
  shippingFee: 3000,
  freeShippingThreshold: 30000,
  remoteAreaFee: 4000,
  deliveryDays: '2~3일',
  deliveryMethod: '일반택배',
  returnPeriod: 7,
  returnShippingPolicy: '단순 변심의 경우 반품 배송비 구매자 부담',
  returnAddress: '',
  returnContact: '',
};

const ShopSettingsPage = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<ShopSettings>(defaultSettings);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${baseUrl}/api/v1/shop-settings`);
      const result = await response.json();

      if (result.success && result.data) {
        setSettings(result.data);
      } else {
        // 설정이 없으면 기본값 사용
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      notify('설정을 불러오는데 실패했습니다', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (field: keyof ShopSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      [field]:
        field === 'shippingFee' ||
        field === 'freeShippingThreshold' ||
        field === 'remoteAreaFee' ||
        field === 'returnPeriod'
          ? parseInt(value) || 0
          : value,
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${baseUrl}/api/v1/shop-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      const result = await response.json();

      if (result.success) {
        notify('설정이 저장되었습니다', { type: 'success' });
        if (result.data) {
          setSettings(result.data);
        }
      } else {
        notify(result.message || '저장 실패', { type: 'error' });
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      notify('저장 중 오류가 발생했습니다', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LocalShipping sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight={700}>
          배송/환불 설정
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        이 설정은 상품 상세 페이지의 배송/환불 정보에 표시됩니다.
      </Alert>

      {/* 배송 정보 섹션 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <LocalShipping />
            배송 정보
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="기본 배송비"
                type="number"
                value={settings.shippingFee}
                onChange={handleChange('shippingFee')}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                }}
                helperText="기본 배송비 (예: 3000)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="무료배송 기준금액"
                type="number"
                value={settings.freeShippingThreshold}
                onChange={handleChange('freeShippingThreshold')}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                }}
                helperText="이 금액 이상 구매시 무료배송"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="도서산간 추가 배송비"
                type="number"
                value={settings.remoteAreaFee}
                onChange={handleChange('remoteAreaFee')}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                }}
                helperText="제주/도서산간 지역 추가 비용"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="배송 소요일"
                value={settings.deliveryDays}
                onChange={handleChange('deliveryDays')}
                fullWidth
                helperText="예: 2~3일, 주문 후 1~2일"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="배송 방법"
                value={settings.deliveryMethod}
                onChange={handleChange('deliveryMethod')}
                fullWidth
                helperText="예: 일반택배, CJ대한통운"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 환불 정보 섹션 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Refresh />
            교환/반품 정보
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="교환/반품 가능 기간"
                type="number"
                value={settings.returnPeriod}
                onChange={handleChange('returnPeriod')}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">일</InputAdornment>,
                }}
                helperText="수령 후 며칠 이내 (예: 7)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="반품 배송비 정책"
                value={settings.returnShippingPolicy}
                onChange={handleChange('returnShippingPolicy')}
                fullWidth
                multiline
                rows={2}
                helperText="반품 배송비 부담 주체 및 조건"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="반품 주소 (선택)"
                value={settings.returnAddress || ''}
                onChange={handleChange('returnAddress')}
                fullWidth
                helperText="반품 수거지 주소"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="반품 연락처 (선택)"
                value={settings.returnContact || ''}
                onChange={handleChange('returnContact')}
                fullWidth
                helperText="고객센터 또는 반품 담당 연락처"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 저장 버튼 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? '저장 중...' : '설정 저장'}
        </Button>
      </Box>
    </Box>
  );
};

export default ShopSettingsPage;
